import { useCallback, useEffect, useState } from "react";
import { FileXlsIcon, XIcon } from "@phosphor-icons/react";
import * as XLSX from "xlsx";

import { Toast } from "components/toast";
import Dropzone from "components/v2/Dropzone";

import { BALANCES_STRINGS } from "../../constants/strings";

import type { AccrualRowWithIndexT } from "../../domain/types/accrual.types";

import {
  TEMPLATE_COLUMNS,
  useDownloadTemplate,
} from "../../hooks/useDownloadTemplate";

import type { CollaboratorUiT } from "shared/domain/ui/collaborator.model";
import { formatBytes } from "shared/helpers/file";
import { normalizeExcelDate } from "shared/helpers/date";

const MAX_FILE_SIZE = 50 * 1024 * 1024;
const HEADER_ROWS_COUNT = 2;
const CONTENT_ROWS_COUNT = 4;

const linkClassName = "text-secondary-PCS001 underline font-semibold";

const ImportTemplateStep = ({
  collaborators,
  sort,
  onDisableNextButton,
  onDataParsed,
}: {
  collaborators: CollaboratorUiT[];
  sort: unknown;
  onDisableNextButton: (disabled: boolean) => void;
  onDataParsed: (rows: AccrualRowWithIndexT[]) => void;
}) => {
  const { downloadTemplate, isDownloading } = useDownloadTemplate();

  useEffect(() => {
    onDisableNextButton(true);
  }, [onDisableNextButton]);

  const [uploadedFile, setUploadedFile] = useState<{
    file: File;
    error?: string;
  }>();

  const handleOnDropFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      const file = files[0];

      if (file.size > MAX_FILE_SIZE) {
        Toast(
          "warning",
          "Su archivo supera el máximo permitido de 50 MB, puede dividirlo en dos archivos o quitar información",
        );

        return;
      }

      setUploadedFile({ file });

      const data = await file.arrayBuffer();

      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, {
        header: TEMPLATE_COLUMNS.map((column) => column.key),
        defval: "",
      });

      const headers = jsonData[HEADER_ROWS_COUNT - 1] as Record<string, string>;

      const hasAllHeaders = TEMPLATE_COLUMNS.every(
        (column) =>
          headers[column.key]?.toString().toLowerCase() ===
          column.title.toLowerCase(),
      );

      if (!hasAllHeaders) {
        onDisableNextButton(true);
        onDataParsed([]);
        setUploadedFile({
          file,
          error:
            "El formato del archivo no es correcto, por favor descargue la plantilla y asegúrese de no modificar los encabezados.",
        });

        return;
      }

      const rows = jsonData.slice(HEADER_ROWS_COUNT) as Record<
        string,
        string
      >[];
      const MANDATORY_KEYS = [
        "code",
        "collaborator",
        "description",
        "vacationDaysAccrued",
      ];

      const hasExtraData = rows.some((row) =>
        MANDATORY_KEYS.every((key) => row[key] !== ""),
      );

      if (!hasExtraData) {
        onDisableNextButton(true);
        onDataParsed([]);
        setUploadedFile({
          file,
          error:
            "No se encontraron datos a importar, por favor llene el documento y vuelva a intentarlo.",
        });

        return;
      }

      const normalize = (value: string) => String(value).trim().toLowerCase();

      const collaboratorKey = (code: string, fullName: string) =>
        `${normalize(code)}|${normalize(fullName)}`;

      const collaboratorIdByCodeAndName = new Map(
        collaborators
          .filter((collaborator) => collaborator.code && collaborator.fullName)
          .map((collaborator) => [
            collaboratorKey(
              collaborator.code as string,
              collaborator.fullName as string,
            ),
            collaborator.id,
          ]),
      );

      const resolveCollaboratorId = (row: Record<string, string>) =>
        collaboratorIdByCodeAndName.get(
          collaboratorKey(row.code, row.collaborator),
        );

      const hasUnmatchedCollaborator = rows.some(
        (row) => !resolveCollaboratorId(row),
      );

      if (hasUnmatchedCollaborator) {
        onDisableNextButton(true);
        onDataParsed([]);
        setUploadedFile({
          file,
          error:
            "La información de uno o más colaboradores no es válida. Verifique los datos e intente de nuevo.",
        });

        return;
      }

      const rowsWithIndex: AccrualRowWithIndexT[] = rows.map((row, index) => ({
        collaboratorId: resolveCollaboratorId(row) as string,
        description: row.description,
        admissionDate: normalizeExcelDate(row.admissionDate),
        vacationDaysAccrued: row.vacationDaysAccrued,
        vacationDaysTaken: row.vacationDaysTaken,
   
        // TODO: descomentar para reactivar.  % prima y días prima ya no vienen en la plantilla
        vacationPremiumPct: "0", // row.vacationPremiumPct,
        vacationPremiumDays: "0", // row.vacationPremiumDays,
        isInitialBalance: true,
        rowNumber: index + CONTENT_ROWS_COUNT,
      }));

      onDisableNextButton(false);
      onDataParsed(rowsWithIndex);
      setUploadedFile({ file });
    },
    [collaborators, onDisableNextButton, onDataParsed],
  );

  const handleRemoveUploadedFile = () => {
    onDisableNextButton(true);
    onDataParsed([]);
    setUploadedFile(null);
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-secondaryText">
        <button
          className={`${linkClassName} cursor-pointer`}
          onClick={(e) => {
            e.preventDefault();
            downloadTemplate({ collaborators, sort: sort as unknown[] });
          }}
          disabled={isDownloading}
        >
          {isDownloading
            ? BALANCES_STRINGS.IMPORT_TEMPLATE_DOWNLOADING
            : BALANCES_STRINGS.IMPORT_TEMPLATE_DOWNLOAD_LINK_TEXT}
        </button>
        {BALANCES_STRINGS.IMPORT_TEMPLATE_INSTRUCTIONS}
      </p>
      {uploadedFile ? (
        <>
          <article
            aria-label={`Archivo adjunto: ${uploadedFile.file.name}`}
            className="flex bg-secondary-PCS001/5 rounded-lg p-2 gap-2 items-center"
          >
            <div aria-hidden="true" className="bg-white p-1 rounded-xl">
              <FileXlsIcon size={30} className="text-secondary-PCS001" />
            </div>

            <dl className="flex flex-col flex-1 min-w-0">
              <dt className="sr-only">Nombre</dt>
              <dd title={uploadedFile.file.name} className="truncate font-bold">
                {uploadedFile.file.name}
              </dd>

              {!!uploadedFile.file.size && (
                <>
                  <dt className="sr-only">Tamaño</dt>
                  <dd className="text-secondaryText text-sm">
                    {formatBytes(uploadedFile.file.size)}
                  </dd>
                </>
              )}
            </dl>

            <button
              type="button"
              aria-label={`Remover archivo: ${uploadedFile.file.name}`}
              className="h-12 w-12 shrink-0 p-1 rounded-full hover:bg-gray-200 transition-colors flex items-center justify-center"
              onClick={handleRemoveUploadedFile}
            >
              <XIcon aria-hidden="true" size={20} />
            </button>
          </article>
          {uploadedFile.error && (
            <p role="alert" className="text-sm text-red-500">
              {uploadedFile.error}
            </p>
          )}
        </>
      ) : (
        <Dropzone
          ariaLabel={BALANCES_STRINGS.IMPORT_TEMPLATE_UPLOAD_AREA_LABEL}
          maxSize={MAX_FILE_SIZE}
          maxFiles={1}
          accept={{
            "application/vnd.ms-excel": [".xls"],
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
              [".xlsx"],
            "text/csv": [".csv"],
          }}
          onDropFiles={handleOnDropFiles}
        >
          <Dropzone.Icon />
          <Dropzone.MainMessage>
            {BALANCES_STRINGS.IMPORT_TEMPLATE_UPLOAD_TITLE_PREFIX}{" "}
            <span className={linkClassName}>
              {BALANCES_STRINGS.IMPORT_TEMPLATE_UPLOAD_TITLE_LINK}
            </span>{" "}
            {BALANCES_STRINGS.IMPORT_TEMPLATE_UPLOAD_TITLE_SUFFIX}
          </Dropzone.MainMessage>
          <Dropzone.SecondaryMessage>
            {BALANCES_STRINGS.IMPORT_TEMPLATE_UPLOAD_FORMATS}
          </Dropzone.SecondaryMessage>
        </Dropzone>
      )}
    </div>
  );
};

export default ImportTemplateStep;
